import React from 'react';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { RevealList, RevealItem } from '../components/Reveal';
import { useAccountSession } from './LogoHeader';
import '../assets/styles/footer.css';

const Footer = () => {
  // Same auth read as the header, so the footer never contradicts it.
  const { status, firstName } = useAccountSession();
  const accountLabel = status === 'signed-in'
    ? (firstName ? `${firstName}'s account` : 'My account')
    : 'Sign in';
  // Stays neutral until the session resolves, so a signed-in client is never
  // announced as "sign in".
  const accountAria = status === 'signed-in'
    ? 'My account'
    : status === 'signed-out' ? 'Sign in to your account' : 'Your account';

  return (
    <footer className="site-footer">
      <RevealList className="footer-inner" stagger={0.12}>
        <RevealItem variant="up">
        <div className="footer-brand">
          <img
            src="/images/reducedPics/navbar_logo_transparent.webp"
            alt="Beauty by Viktoria"
            className="footer-logo"
          />
          <p className="footer-tagline">Premium beauty treatments in the heart of Nelson, New Zealand.</p>
          <div className="footer-social">
            <a
              href="https://www.instagram.com/beauty_byviktoria/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="footer-social-link"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </a>
            <a
              href="https://www.facebook.com/Viktoriahouseofbeauty"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="footer-social-link"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
            </a>
          </div>
        </div>
        </RevealItem>

        <RevealItem variant="up">
        <div className="footer-col">
          <h3 className="footer-col-title">Quick Links</h3>
          <nav aria-label="Footer navigation">
            <Link to="/">Home</Link>
            <Link to="/price">Prices</Link>
            <Link to="/shop">Shop</Link>
            <Link to="/book">Book Now</Link>
            {/* One link in both states — /account signs you in or shows your
                appointments. The row keeps its height while the session
                resolves so the footer never reflows. */}
            <Link to="/account" className="footer-account-link" aria-label={accountAria}>
              <UserRound size={15} strokeWidth={1.9} aria-hidden="true" />
              <span className="footer-account-text">
                {status === 'loading'
                  ? <span className="footer-account-placeholder" aria-hidden="true" />
                  : accountLabel}
              </span>
            </Link>
          </nav>
        </div>
        </RevealItem>

        <RevealItem variant="up">
        <div className="footer-col">
          <h3 className="footer-col-title">Contact</h3>
          <p>Nelson, New Zealand</p>
          <p>Book online or via social media</p>
          <Link to="/book" className="footer-book-cta">Book an Appointment</Link>
        </div>
        </RevealItem>
      </RevealList>

      <div className="footer-bottom">
        <div className="footer-legal">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms &amp; Conditions</Link>
          <Link to="/returns">Returns &amp; Refunds</Link>
        </div>
        <p className="footer-copy">
          &copy; {new Date().getFullYear()} Beauty by Viktoria. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
